// src/Sectors.jsx
import React, { useState, useEffect } from "react";
import axios from "axios";

import { Collapse, Tag, Input, Row, Col, Card } from "antd";
import {
  ThunderboltOutlined,
  CloudOutlined,
  ShopOutlined,
  HeartOutlined,
  ShoppingOutlined,
  BuildOutlined,
  HomeOutlined,
  ApiOutlined,
} from "@ant-design/icons";
import PropTypes from "prop-types";
import "./App.css";

const { Panel } = Collapse;
const { Search } = Input;

// Top 3 sectors
const topSectorMeta = [
  { key: "AI Solutions", icon: <ApiOutlined /> },
  { key: "Renewable Energy", icon: <ThunderboltOutlined /> },
  { key: "Wealth Management", icon: <ShopOutlined /> },
];

// Full industry list
const allSectors = [
  "AI Solutions",
  "Renewable Energy",
  "Wealth Management",
  "Financial Services",
  "E-commerce",
  "Real Estate",
  "Data Centers",
  "Natural Gas",
  "Cybersecurity",
  "Cloud Services",
  "Digital Marketing",
  "Biopharmaceuticals",
  "Insurance Services",
  "Retail Banking",
  "Healthcare",
  "Industrial Automation",
  "Mobile Banking",
  "Asset Management",
  "Payment Processing",
  "Real Estate Investment",
  "Logistics",
  "Cloud Solutions",
  "Consumer Finance",
  "Infrastructure",
  "Energy Transition",
  "Energy Management",
  "Supply Chain",
  "Investment Banking",
  "Life Sciences",
  "Digital Health",
  "Commercial Banking",
  "Drug Development",
  "Artificial Intelligence",
  "Automation",
  "Sustainability Initiatives",
  "Machine Learning",
  "Private Equity",
  "Blockchain Technology",
  "Digital Advertising",
  "Direct-to-Consumer",
  "Pharmaceuticals",
  "Retail Stores",
  "Commercial Real Estate",
  "Insurance Products",
  "Biotechnology",
  "Cloud Computing",
  "Medical Devices",
  "Energy Storage",
  "Software Solutions",
  "Gene Therapy",
  "Consumer Electronics",
  "Carbon Capture",
  "Construction Materials",
  "Digital Transformation",
  "E-mobility",
  "Aerospace",
  "Investment Advisory",
  "Customer Experience",
  "Banking Services",
  "Subscription Services",
  "Healthcare Innovation",
  "Smart Manufacturing",
];

// Grouped panels
const sectorGroups = [
  {
    key: "energy",
    header: (
      <>
        <ThunderboltOutlined /> Energy & Utilities
      </>
    ),
    items: [
      "Renewable Energy",
      "Natural Gas",
      "Energy Transition",
      "Energy Management",
      "Energy Storage",
      "Carbon Capture",
    ],
  },
  {
    key: "technology",
    header: (
      <>
        <CloudOutlined /> Technology & AI
      </>
    ),
    items: [
      "AI Solutions",
      "Artificial Intelligence",
      "Machine Learning",
      "Automation",
      "Software Solutions",
      "Cloud Computing",
      "Cloud Solutions",
      "Cloud Services",
      "Data Centers",
      "Cybersecurity",
      "Blockchain Technology",
    ],
  },
  {
    key: "finance",
    header: (
      <>
        <ShopOutlined /> Finance & Banking
      </>
    ),
    items: [
      "Wealth Management",
      "Financial Services",
      "Asset Management",
      "Private Equity",
      "Investment Banking",
      "Banking Services",
      "Commercial Banking",
      "Retail Banking",
      "Consumer Finance",
      "Payment Processing",
      "Insurance Services",
      "Insurance Products",
      "Investment Advisory",
    ],
  },
  {
    key: "healthcare",
    header: (
      <>
        <HeartOutlined /> Healthcare & Biotech
      </>
    ),
    items: [
      "Healthcare",
      "Biopharmaceuticals",
      "Biotechnology",
      "Medical Devices",
      "Drug Development",
      "Gene Therapy",
      "Life Sciences",
      "Digital Health",
      "Healthcare Innovation",
    ],
  },
  {
    key: "consumer",
    header: (
      <>
        <ShoppingOutlined /> Consumer & Retail
      </>
    ),
    items: [
      "E-commerce",
      "Retail Stores",
      "Direct-to-Consumer",
      "Subscription Services",
      "Consumer Electronics",
      "Digital Advertising",
      "Digital Marketing",
      "Customer Experience",
    ],
  },
  {
    key: "real-estate",
    header: (
      <>
        <HomeOutlined /> Real Estate & Infrastructure
      </>
    ),
    items: [
      "Real Estate",
      "Commercial Real Estate",
      "Real Estate Investment",
      "Infrastructure",
      "Construction Materials",
    ],
  },
  {
    key: "industrials",
    header: (
      <>
        <BuildOutlined /> Industrials & Logistics
      </>
    ),
    items: [
      "Industrial Automation",
      "Smart Manufacturing",
      "Logistics",
      "Supply Chain",
      "E-mobility",
      "Aerospace",
    ],
  },
  {
    key: "sustainability",
    header: (
      <>
        <ApiOutlined /> Sustainability & Transformation
      </>
    ),
    items: ["Sustainability Initiatives", "Digital Transformation"],
  },
];

const Sectors = ({ isMobile }) => {
  const [filter, setFilter] = useState("");
  const [visibleTags, setVisibleTags] = useState(allSectors);

  useEffect(() => {
    const lower = filter.toLowerCase();
    setVisibleTags(
      allSectors.filter((sec) => sec.toLowerCase().includes(lower))
    );
  }, [filter]);

  const API_URL = "https://ixvr1ju471.execute-api.us-east-1.amazonaws.com/prod";

  const openSectorLink = async (sectorName) => {
    const slug = sectorName.toLowerCase().replace(/\s+/g, "-");
    const path = `blogs/${slug}/sector-analysis-of-${slug}-and-what-it-means-for-investors`;
    const fullUrl = `https://www.stockbuzz.ai/${path}`;

    try {
      const resp = await axios.post(
        API_URL,
        {
          queryStringParameters: {
            url: `sector-analysis-of-${slug}-and-what-it-means-for-investors`,
          },
        },
        { headers: { "Content-Type": "application/json" } }
      );
      const raw = JSON.parse(resp.data.body);
      if (raw?.description) {
        // SEO page exists
        window.open(fullUrl, "_blank");
        return;
      }
    } catch (e) {
      console.warn("SEO check failed, falling back to question", e);
    }

    // fallback to ask-a-question URL
    const q = encodeURIComponent(
      `Analyze Top Companies in ${sectorName} Sector`
    );
    window.open(`https://www.stockbuzz.ai/?question=${q}`, "_blank");
  };

  return (
    <div
      style={{
        padding: "2% 2% 10% 2%",
        marginTop: isMobile ? "20%" : "5%",
        marginBottom: isMobile ? "15%" : 0,
      }}
    >
      <h2>Top Sectors</h2>
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        {topSectorMeta.map(({ key, icon }) => (
          <Col xs={24} sm={12} md={8} key={key}>
            <Card
              className="top-sector-card"
              bordered={false}
              onClick={() => openSectorLink(key)}
            >
              <div className="top-sector-content">
                {icon}
                <span className="top-sector-label">{key}</span>
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      <Collapse accordion defaultActiveKey={["energy"]}>
        {sectorGroups.map(({ key, header, items }) => (
          <Panel header={header} key={key}>
            <Row gutter={[8, 8]}>
              {items.map((sec) => (
                <Col key={sec}>
                  <Tag
                    color="default"
                    onClick={() => openSectorLink(sec)}
                    style={{ cursor: "pointer" }}
                  >
                    {sec}
                  </Tag>
                </Col>
              ))}
            </Row>
          </Panel>
        ))}
      </Collapse>

      <h2>All Sectors</h2>
      <Search
        placeholder="Filter sectors…"
        allowClear
        onChange={(e) => setFilter(e.target.value)}
        style={{
          marginBottom: 16,
          width: isMobile ? "100%" : 300,
        }}
      />
      <Row gutter={[8, 8]} style={{ marginBottom: 24 }}>
        {visibleTags.map((sec) => (
          <Col key={sec}>
            <Tag className="sector-tag" onClick={() => openSectorLink(sec)}>
              {sec}
            </Tag>
          </Col>
        ))}
      </Row>
    </div>
  );
};

Sectors.propTypes = {
  isMobile: PropTypes.bool.isRequired,
};

export default Sectors;
